import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../config/api';
import { FiMail, FiSend, FiFile, FiTrash2, FiEye, FiCornerUpLeft, FiPlus, FiSearch } from 'react-icons/fi';
import { FaEnvelope, FaInbox } from 'react-icons/fa';
import { Email, EmailAccount } from '../../types/email';

const EmailList: React.FC = () => {
	const { token } = useAuth();
	const navigate = useNavigate();
	const [emails, setEmails] = useState<Email[]>([]);
	const [filteredEmails, setFilteredEmails] = useState<Email[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedStatus, setSelectedStatus] = useState<'all' | 'sent' | 'draft' | 'failed'>('all');
	
	// Email account states
	const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
	const [selectedMailbox, setSelectedMailbox] = useState<string>('all');

	useEffect(() => {
		fetchEmails();
		fetchEmailAccounts();
	}, [token]);

	useEffect(() => {
		filterEmails();
	}, [emails, searchTerm, selectedStatus, selectedMailbox]);

	const fetchEmails = async () => {
		if (!token) return;
		
		try {
			setLoading(true);
			const response = await apiCall(ENDPOINTS.EMAIL.LIST, {
				method: 'GET',
			}, token);

			if (response.ok) {
				const data = await response.json();
				setEmails(data.results || data || []);
			}
		} catch (error) {
			console.error('Error fetching emails:', error);
		} finally {
			setLoading(false);
		}
	};
	
	const fetchEmailAccounts = async () => {
		if (!token) return;
		
		try {
			const response = await apiCall(ENDPOINTS.EMAIL.ACCOUNTS.ACTIVE, {
				method: 'GET',
			}, token);

			if (response.ok) {
				const data = await response.json();
				setEmailAccounts(data || []);
			}
		} catch (error) {
			console.error('Error fetching email accounts:', error);
		}
	};

	const filterEmails = () => {
		let filtered = emails;

		// Filter by status
		if (selectedStatus !== 'all') {
			filtered = filtered.filter(e => e.status === selectedStatus);
		}
		
		// Filter by mailbox (email account)
		if (selectedMailbox !== 'all') {
			filtered = filtered.filter(e => e.from_email === selectedMailbox);
		}

		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter(e =>
				e.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
				e.from_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				e.to_recipients.some(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()))
			);
		}

		// Sort by created_at descending
		filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

		setFilteredEmails(filtered);
	};

	const handleCompose = () => {
		navigate('/email/compose');
	};

	const handleViewEmail = (email: Email) => {
		setSelectedEmail(email);
	};

	const handleReply = (email: Email) => {
		navigate('/email/compose', { state: { replyToEmail: email } });
	};

	const handleDelete = async (id: string) => {
		if (!token) return;
		if (!window.confirm('Are you sure you want to delete this email?')) {
			return;
		}

		try {
			const response = await apiCall(`${ENDPOINTS.EMAIL.DETAIL}${id}/`, {
				method: 'DELETE',
			}, token);

			if (response.ok) {
				fetchEmails();
				if (selectedEmail?.id === id) {
					setSelectedEmail(null);
				}
			}
		} catch (error) {
			console.error('Error deleting email:', error);
			alert('Failed to delete email');
		}
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

		if (diffDays === 0) {
			return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
		} else if (diffDays === 1) {
			return 'Yesterday';
		} else if (diffDays < 7) {
			return date.toLocaleDateString('en-AU', { weekday: 'short' });
		} else {
			return date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
		}
	};

	const getStatusBadge = (status: Email['status']) => {
		const badges = {
			sent: <span className="badge badge-success badge-sm">Sent</span>,
			draft: <span className="badge badge-warning badge-sm">Draft</span>,
			failed: <span className="badge badge-error badge-sm">Failed</span>,
			scheduled: <span className="badge badge-info badge-sm">Scheduled</span>,
		};
		return badges[status] || null;
	};

	return (
		<div className="h-screen flex flex-col">
			{/* Header */}
			<div className="bg-base-200 p-4 shadow-lg flex-shrink-0">
				<div className="flex items-center justify-between gap-4">
					<h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
						<FaEnvelope className="text-primary" />
						Email
					</h1>
					<button
						onClick={handleCompose}
						className="btn btn-primary gap-2"
					>
						<FiPlus className="w-5 h-5" />
						Compose
					</button>
				</div>

				{/* Search and Filter */}
				<div className="mt-4 flex flex-col sm:flex-row gap-4">
					{/* Mailbox Selector */}
					<div className="form-control min-w-[200px]">
						<select
							className="select select-bordered"
							value={selectedMailbox}
							onChange={(e) => setSelectedMailbox(e.target.value)}
						>
							<option value="all">
								<FaInbox /> All Mailboxes
							</option>
							{emailAccounts.map((account) => (
								<option key={account.id} value={account.email_address}>
									{account.name} ({account.email_address})
								</option>
							))}
						</select>
					</div>
					
					{/* Search */}
					<div className="flex-1 relative">
						<input
							type="text"
							placeholder="Search emails..."
							className="input input-bordered w-full pr-10"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
						<button className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-primary btn-sm btn-square">
							<FiSearch className="w-4 h-4" />
						</button>
					</div>

					{/* Status Filter */}
					<div className="flex items-center gap-4">
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="all"
								checked={selectedStatus === 'all'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-sm radio-primary"
							/>
							<span className="text-sm">All</span>
						</label>
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="sent"
								checked={selectedStatus === 'sent'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-sm radio-primary"
							/>
							<span className="text-sm">Sent</span>
						</label>
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="draft"
								checked={selectedStatus === 'draft'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-sm radio-primary"
							/>
							<span className="text-sm">Drafts</span>
						</label>
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="failed"
								checked={selectedStatus === 'failed'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-sm radio-primary"
							/>
							<span className="text-sm">Failed</span>
						</label>
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="flex-1 flex overflow-hidden">
				{/* Email List */}
				<div className="w-96 border-r border-base-300 flex flex-col">
					<div className="p-4 border-b border-base-300 bg-base-200">
						<h2 className="font-semibold">
							{selectedStatus === 'all' ? 'All Emails' : selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)}
							<span className="ml-2 text-sm opacity-60">({filteredEmails.length})</span>
						</h2>
					</div>

					<div className="flex-1 overflow-y-auto">
						{loading ? (
							<div className="flex justify-center items-center h-full">
								<span className="loading loading-spinner loading-lg"></span>
							</div>
						) : filteredEmails.length === 0 ? (
							<div className="flex flex-col items-center justify-center h-full text-center p-8">
								<FiMail className="w-16 h-16 opacity-20 mb-4" />
								<p className="text-lg opacity-60">No emails found</p>
								<p className="text-sm opacity-40 mt-2">
									{searchTerm ? 'Try adjusting your search' : 'Click "Compose" to send your first email'}
								</p>
							</div>
						) : (
							filteredEmails.map((email) => (
								<div
									key={email.id}
									onClick={() => handleViewEmail(email)}
									className={`flex items-start p-4 border-b border-base-300 cursor-pointer hover:bg-base-200 transition-colors ${
										selectedEmail?.id === email.id ? 'bg-primary/10' : ''
									}`}
								>
									<div className="flex-1 min-w-0">
										<div className="flex items-center justify-between mb-1">
											<span className="font-semibold truncate">
												{email.status === 'sent' ? (
													email.to_recipients.length > 0 
														? `To: ${email.to_recipients[0].name}`
														: 'Unknown Recipient'
												) : (
													'Draft'
												)}
											</span>
											<span className="text-xs opacity-60 ml-2 whitespace-nowrap">
												{formatDate(email.created_at)}
											</span>
										</div>
										<div className="text-sm font-medium truncate mb-1">{email.subject || '(No Subject)'}</div>
										<div className="flex items-center gap-2">
											{getStatusBadge(email.status)}
											{email.attachments && email.attachments.length > 0 && (
												<span className="text-xs opacity-60 flex items-center gap-1">
													<FiFile className="w-3 h-3" />
													{email.attachments.length}
												</span>
											)}
										</div>
									</div>
								</div>
							))
						)}
					</div>
				</div>

				{/* Email Viewer */}
				<div className="flex-1 flex flex-col">
					{selectedEmail ? (
						<>
							{/* Email Header */}
							<div className="p-6 border-b border-base-300 bg-base-100">
								<div className="flex items-start justify-between mb-4">
									<h2 className="text-2xl font-bold">{selectedEmail.subject || '(No Subject)'}</h2>
									<div className="flex items-center gap-2">
										{selectedEmail.status === 'sent' && (
											<button
												onClick={() => handleReply(selectedEmail)}
												className="btn btn-ghost btn-sm gap-2"
											>
												<FiCornerUpLeft className="w-4 h-4" />
												Reply
											</button>
										)}
										<button
											onClick={() => handleDelete(selectedEmail.id)}
											className="btn btn-ghost btn-sm btn-square text-error"
										>
											<FiTrash2 className="w-4 h-4" />
										</button>
									</div>
								</div>

								<div className="space-y-2 text-sm">
									<div className="flex items-start">
										<span className="font-semibold w-20">From:</span>
										<span>{selectedEmail.from_name} &lt;{selectedEmail.from_email}&gt;</span>
									</div>
									<div className="flex items-start">
										<span className="font-semibold w-20">To:</span>
										<div className="flex-1">
											{selectedEmail.to_recipients.map((r, i) => (
												<span key={i}>
													{r.name} &lt;{r.email}&gt;
													{i < selectedEmail.to_recipients.length - 1 && ', '}
												</span>
											))}
										</div>
									</div>
									{selectedEmail.cc_recipients && selectedEmail.cc_recipients.length > 0 && (
										<div className="flex items-start">
											<span className="font-semibold w-20">Cc:</span>
											<div className="flex-1">
												{selectedEmail.cc_recipients.map((r, i) => (
													<span key={i}>
														{r.name} &lt;{r.email}&gt;
														{i < selectedEmail.cc_recipients!.length - 1 && ', '}
													</span>
												))}
											</div>
										</div>
									)}
									<div className="flex items-start">
										<span className="font-semibold w-20">Date:</span>
										<span>{new Date(selectedEmail.created_at).toLocaleString('en-AU')}</span>
									</div>
									{selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
										<div className="flex items-start">
											<span className="font-semibold w-20">Attachments:</span>
											<div className="flex flex-wrap gap-2">
												{selectedEmail.attachments.map((att, i) => (
													<div key={i} className="badge badge-outline gap-1">
														<FiFile className="w-3 h-3" />
														{att.file_name}
													</div>
												))}
											</div>
										</div>
									)}
								</div>
							</div>

							{/* Email Body */}
							<div 
								className="flex-1 p-6 overflow-y-auto prose max-w-none"
								dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
							/>
						</>
					) : (
						<div className="flex-1 flex items-center justify-center opacity-60">
							<div className="text-center">
								<FiMail className="w-24 h-24 mx-auto mb-4 opacity-20" />
								<p>Select an email to view</p>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default EmailList;
